import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import axiosInstance from "../axiosInstance";

const PROVIDERS = ["Shopify", "WooCommerce", "FluentCommerce"];

const MAPPING_FIELDS = [
  { key: "trackingNumber", label: "Tracking number" },
  { key: "trackingUrl", label: "Tracking URL" },
  { key: "carrier", label: "Carrier" },
  { key: "itemStatus", label: "Item status" },
  { key: "estimatedFulfillmentDate", label: "Estimated fulfillment date" },
];

const MAPPING_PROPERTIES = [
  { key: "fieldPath", label: "Field path" },
  { key: "pattern", label: "Pattern" },
  { key: "matchGroup", label: "Match group" },
];

const createEmptyMapping = () => ({
  fieldPath: "",
  pattern: "",
  matchGroup: "",
});

const createEmptyForm = (provider = "") => ({
  provider,
  wooCommerce: {
    storeUrl: "",
    ...Object.fromEntries(
      MAPPING_FIELDS.map(({ key }) => [key, createEmptyMapping()])
    ),
  },
  consumerKey: "",
  consumerSecret: "",
  fluentCommerce: { storeUrl: "" },
  clientId: "",
  clientSecret: "",
  cloudTenantId: "",
});

const valueOrEmpty = (value) =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const normalizeConfiguration = (data) => {
  const suppliedProvider = valueOrEmpty(data?.configuration?.provider);
  const provider = PROVIDERS.find(
    (candidate) => candidate.toLowerCase() === suppliedProvider.toLowerCase()
  ) || "";

  if (suppliedProvider && !provider) {
    throw new Error(
      `Unsupported order tracking provider "${suppliedProvider}". Please contact the API owner.`
    );
  }

  const normalized = createEmptyForm(provider);
  normalized.fluentCommerce.storeUrl = valueOrEmpty(
    data?.configuration?.fluentCommerce?.storeUrl
  );
  const wooCommerce = data?.configuration?.wooCommerce;

  if (wooCommerce && typeof wooCommerce === "object") {
    normalized.wooCommerce.storeUrl = valueOrEmpty(wooCommerce.storeUrl);
    MAPPING_FIELDS.forEach(({ key }) => {
      const mapping = wooCommerce[key];
      if (!mapping || typeof mapping !== "object") return;

      MAPPING_PROPERTIES.forEach(({ key: propertyKey }) => {
        normalized.wooCommerce[key][propertyKey] = valueOrEmpty(
          mapping[propertyKey]
        );
      });
    });
  }

  if (provider === "FluentCommerce") {
    normalized.clientId = valueOrEmpty(data?.consumerKey);
    normalized.clientSecret = valueOrEmpty(data?.consumerSecret);
    normalized.cloudTenantId = valueOrEmpty(data?.cloudTenantId);
  } else {
    normalized.consumerKey = valueOrEmpty(data?.consumerKey);
    normalized.consumerSecret = valueOrEmpty(data?.consumerSecret);
  }
  return normalized;
};

const compactOptionalValues = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  return Object.entries(value).reduce((result, [key, childValue]) => {
    if (typeof childValue === "string") {
      if (childValue.trim()) result[key] = childValue;
      return result;
    }

    const compactedChild = compactOptionalValues(childValue);
    if (
      compactedChild &&
      typeof compactedChild === "object" &&
      Object.keys(compactedChild).length > 0
    ) {
      result[key] = compactedChild;
    }
    return result;
  }, {});
};

export const buildOrderTrackingPayload = (form) => {
  if (form.provider === "Shopify") {
    return { configuration: { provider: "Shopify" } };
  }

  if (form.provider === "FluentCommerce") {
    return {
      configuration: {
        provider: "FluentCommerce",
        fluentCommerce: {
          storeUrl: form.fluentCommerce.storeUrl.trim(),
        },
      },
      consumerKey: form.clientId,
      consumerSecret: form.clientSecret,
      cloudTenantId: form.cloudTenantId,
    };
  }

  const wooCommerce = compactOptionalValues(form.wooCommerce);
  const payload = {
    configuration: {
      provider: "WooCommerce",
      ...(Object.keys(wooCommerce).length > 0 ? { wooCommerce } : {}),
    },
  };

  payload.consumerKey = form.consumerKey.trim() ? form.consumerKey : "";
  payload.consumerSecret = form.consumerSecret.trim()
    ? form.consumerSecret
    : "";
  return payload;
};

export const getOrderTrackingUrl = (tenantId) => {
  const baseUrl = (axiosInstance.defaults.baseURL || "").replace(/\/+$/, "");
  return `${baseUrl}/tenant/${encodeURIComponent(tenantId)}/order-tracking`;
};

const getApiErrorMessage = (error, fallback) => {
  const responseMessage = error?.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }
  if (typeof error?.response?.data === "string" && error.response.data.trim()) {
    return error.response.data;
  }
  if (error?.message && !error?.response) return error.message;
  return fallback;
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "#fff",
  },
};

const cardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(255, 132, 76, 0.12)",
  backgroundColor: "rgba(255, 250, 247, 0.74)",
};

const PrefixMaskedInput = React.forwardRef(function PrefixMaskedInput(
  { value, visiblePrefixLength, revealed, style, onScroll, ...inputProps },
  ref
) {
  const [scrollLeft, setScrollLeft] = useState(0);
  const characters = Array.from(value || "");
  const previewValue = characters.length <= visiblePrefixLength + 1
    ? "•".repeat(characters.length)
    : (
      characters.slice(0, visiblePrefixLength).join("") +
      "•".repeat(characters.length - visiblePrefixLength - 1) +
      characters[characters.length - 1]
    ).replace(/\*/g, "•");
  const maskedValue = revealed ? previewValue : "•".repeat(characters.length);
  delete inputProps.ownerState;

  return (
    <Box component="span" sx={{ position: "relative", flex: 1, minWidth: 0 }}>
      {/* Keep the native input value intact; the overlay only changes its appearance. */}
      <input
        {...inputProps}
        ref={ref}
        value={value}
        onScroll={(event) => {
          setScrollLeft(event.currentTarget.scrollLeft);
          onScroll?.(event);
        }}
        style={{
          ...style,
          boxSizing: "border-box",
          height: "auto",
          fontFamily: "monospace",
          color: "transparent",
          WebkitTextFillColor: "transparent",
          caretColor: "#202531",
        }}
      />
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: "0 0 0 14px",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          pointerEvents: "none",
          userSelect: "none",
          fontFamily: "monospace",
        }}
      >
        <span style={{ whiteSpace: "pre", transform: `translateX(-${scrollLeft}px)` }}>
          {maskedValue}
        </span>
      </Box>
    </Box>
  );
});

function CredentialField({
  label,
  value,
  onChange,
  revealed,
  onToggle,
  required = false,
  error = false,
  helperText,
  visiblePrefixLength = 0,
  toggleDisabled = false,
}) {
  const fieldName = label.toLowerCase();

  return (
    <TextField
      fullWidth
      required={required}
      label={label}
      type={revealed && visiblePrefixLength === 0 ? "text" : "password"}
      value={value}
      onChange={onChange}
      autoComplete="off"
      error={error}
      helperText={helperText}
      sx={fieldSx}
      InputProps={{
        ...(visiblePrefixLength > 0 && {
          inputComponent: PrefixMaskedInput,
          inputProps: { visiblePrefixLength, revealed },
        }),
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip
              title={
                toggleDisabled
                  ? "Available after saving"
                  : revealed ? `Hide ${fieldName}` : `Show ${fieldName}`
              }
            >
              <span>
                <IconButton
                  edge="end"
                  disabled={toggleDisabled}
                  onClick={onToggle}
                  aria-label={revealed ? `Hide ${fieldName}` : `Show ${fieldName}`}
                >
                  {revealed ? (
                    <VisibilityOffOutlinedIcon />
                  ) : (
                    <VisibilityOutlinedIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}

export default function OrderTrackingSettings({ tenantId, showHeading = true }) {
  const [form, setForm] = useState(() => createEmptyForm());
  const [savedForm, setSavedForm] = useState(() => createEmptyForm());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [savedAwaitingRefresh, setSavedAwaitingRefresh] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [showConsumerKey, setShowConsumerKey] = useState(false);
  const [showConsumerSecret, setShowConsumerSecret] = useState(false);
  const [showClientId, setShowClientId] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showCloudTenantId, setShowCloudTenantId] = useState(false);
  const [confirmShopifyOpen, setConfirmShopifyOpen] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const activeTenantIdRef = useRef(tenantId);
  const hasSavedClientId = Boolean(savedForm.clientId.trim());
  const hasSavedClientSecret = Boolean(savedForm.clientSecret.trim());
  const hasSavedCloudTenantId = Boolean(savedForm.cloudTenantId.trim());

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(savedForm),
    [form, savedForm]
  );
  const isStoreUrlMissing =
    form.provider === "WooCommerce" && !form.wooCommerce.storeUrl.trim();
  const isConsumerKeyMissing =
    form.provider === "WooCommerce" && !form.consumerKey.trim();
  const isConsumerSecretMissing =
    form.provider === "WooCommerce" && !form.consumerSecret.trim();
  const hasMissingWooCommerceFields =
    isStoreUrlMissing || isConsumerKeyMissing || isConsumerSecretMissing;
  const isClientIdMissing =
    form.provider === "FluentCommerce" && !form.clientId.trim();
  const isClientSecretMissing =
    form.provider === "FluentCommerce" && !form.clientSecret.trim();
  const isServerUrlMissing =
    form.provider === "FluentCommerce" && !form.fluentCommerce.storeUrl.trim();
  const isCloudTenantIdMissing =
    form.provider === "FluentCommerce" && !form.cloudTenantId.trim();

  const loadConfiguration = useCallback(
    async (signal, afterSave = false) => {
      const isActive = () =>
        !signal?.aborted && activeTenantIdRef.current === tenantId;
      setLoading(true);
      setLoadError("");
      setSaveError("");
      setSuccessOpen(false);

      try {
        const response = await axiosInstance.get(
          getOrderTrackingUrl(tenantId),
          signal ? { signal } : undefined
        );
        if (!isActive()) return;
        const nextForm = normalizeConfiguration(response.data);
        setForm(nextForm);
        setSavedForm(nextForm);
        setShowValidation(false);
        setShowConsumerKey(false);
        setShowConsumerSecret(false);
        setShowClientId(false);
        setShowClientSecret(false);
        setShowCloudTenantId(false);
        setSavedAwaitingRefresh(false);
        if (afterSave) setSuccessOpen(true);
      } catch (error) {
        if (!isActive()) return;
        setLoadError(
          getApiErrorMessage(
            error,
            "Failed to load order tracking settings. Please try again."
          )
        );
      } finally {
        if (isActive()) setLoading(false);
      }
    },
    [tenantId]
  );

  useEffect(() => {
    activeTenantIdRef.current = tenantId;
    setSaving(false);
    setSavedAwaitingRefresh(false);
    setForm(createEmptyForm());
    setSavedForm(createEmptyForm());
    const controller = new AbortController();
    loadConfiguration(controller.signal);
    return () => {
      controller.abort();
      activeTenantIdRef.current = null;
    };
  }, [loadConfiguration, tenantId]);

  const handleProviderChange = (event) => {
    const nextProvider = event.target.value;
    setShowValidation(false);
    setShowClientId(false);
    setShowClientSecret(false);
    setShowCloudTenantId(false);
    if (form.provider === "WooCommerce" && nextProvider === "Shopify") {
      setConfirmShopifyOpen(true);
      return;
    }

    setSaveError("");
    setForm((current) => ({ ...current, provider: nextProvider }));
  };

  const handleWooCommerceChange = (field, value) => {
    setSaveError("");
    setForm((current) => ({
      ...current,
      wooCommerce: {
        ...current.wooCommerce,
        [field]: value,
      },
    }));
  };

  const handleMappingChange = (mappingKey, propertyKey, value) => {
    setSaveError("");
    setForm((current) => ({
      ...current,
      wooCommerce: {
        ...current.wooCommerce,
        [mappingKey]: {
          ...current.wooCommerce[mappingKey],
          [propertyKey]: value,
        },
      },
    }));
  };

  const confirmShopify = () => {
    setForm(createEmptyForm("Shopify"));
    setSaveError("");
    setShowValidation(false);
    setShowConsumerKey(false);
    setShowConsumerSecret(false);
    setConfirmShopifyOpen(false);
  };

  const handleSave = async () => {
    if (!form.provider || saving) return;
    if (
      hasMissingWooCommerceFields ||
      isClientIdMissing ||
      isClientSecretMissing ||
      isServerUrlMissing ||
      isCloudTenantIdMissing
    ) {
      setShowValidation(true);
      return;
    }

    setSaving(true);
    setSaveError("");
    setSuccessOpen(false);
    setShowValidation(false);
    try {
      const payload = buildOrderTrackingPayload(form);
      if (form.provider === "Shopify") {
        // Shopify must be submitted as a provider-only configuration.
        delete payload.configuration.wooCommerce;
        delete payload.wooCommerce;
      }
      await axiosInstance.put(getOrderTrackingUrl(tenantId), payload);
      if (activeTenantIdRef.current !== tenantId) return;
      // PUT omits credentials. Discard submitted secrets and reload the server masks.
      setForm(createEmptyForm());
      setSavedForm(createEmptyForm());
      setSavedAwaitingRefresh(true);
      setShowClientId(false);
      setShowClientSecret(false);
      setShowCloudTenantId(false);
      await loadConfiguration(undefined, true);
    } catch (error) {
      if (activeTenantIdRef.current !== tenantId) return;
      setSaveError(
        getApiErrorMessage(
          error,
          "Failed to save order tracking settings. Please try again."
        )
      );
    } finally {
      if (activeTenantIdRef.current === tenantId) setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={7}
      >
        <CircularProgress sx={{ color: "#f97316" }} />
        <Typography variant="body2" sx={{ mt: 1.5, color: "#6b7280" }}>
          Loading order tracking settings...
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity={savedAwaitingRefresh ? "warning" : "error"}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => loadConfiguration(undefined, savedAwaitingRefresh)}
          >
            Retry
          </Button>
        }
      >
        {savedAwaitingRefresh
          ? `Order tracking settings were saved, but refreshing them failed. Retry to reload the saved settings. ${loadError}`
          : loadError}
      </Alert>
    );
  }

  return (
    <>
      <Box component="form" noValidate onSubmit={(event) => event.preventDefault()}>
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
          {showHeading && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531" }}>
                Order tracking
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: "#6b7280" }}>
                Choose the tenant&apos;s commerce provider and configure how shipment
                details are read.
              </Typography>
            </>
          )}

          {saveError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          ) : null}

          <FormControl fullWidth required sx={fieldSx}>
            <InputLabel id="order-tracking-provider-label">Provider</InputLabel>
            <Select
              labelId="order-tracking-provider-label"
              id="order-tracking-provider"
              label="Provider"
              value={form.provider}
              onChange={handleProviderChange}
            >
              <MenuItem value="Shopify">Shopify</MenuItem>
              <MenuItem value="WooCommerce">WooCommerce</MenuItem>
              <MenuItem value="FluentCommerce">FluentCommerce</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {form.provider === "WooCommerce" ? (
          <>
            <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mt: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, color: "#202531", mb: 2 }}
              >
                WooCommerce connection
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Store URL"
                    value={form.wooCommerce.storeUrl}
                    onChange={(event) =>
                      handleWooCommerceChange("storeUrl", event.target.value)
                    }
                    error={showValidation && isStoreUrlMissing}
                    helperText={
                      showValidation && isStoreUrlMissing
                        ? "Store URL is required for WooCommerce."
                        : undefined
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CredentialField
                    label="Consumer key"
                    visiblePrefixLength={3}
                    value={form.consumerKey}
                    onChange={(event) => {
                      setSaveError("");
                      setForm((current) => ({
                        ...current,
                        consumerKey: event.target.value,
                      }));
                    }}
                    revealed={showConsumerKey}
                    toggleDisabled={!savedForm.consumerKey.trim()}
                    onToggle={() => setShowConsumerKey((current) => !current)}
                    required
                    error={showValidation && isConsumerKeyMissing}
                    helperText={
                      showValidation && isConsumerKeyMissing
                        ? "Consumer key is required for WooCommerce."
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CredentialField
                    label="Consumer secret"
                    visiblePrefixLength={3}
                    value={form.consumerSecret}
                    onChange={(event) => {
                      setSaveError("");
                      setForm((current) => ({
                        ...current,
                        consumerSecret: event.target.value,
                      }));
                    }}
                    revealed={showConsumerSecret}
                    toggleDisabled={!savedForm.consumerSecret.trim()}
                    onToggle={() =>
                      setShowConsumerSecret((current) => !current)
                    }
                    required
                    error={showValidation && isConsumerSecretMissing}
                    helperText={
                      showValidation && isConsumerSecretMissing
                        ? "Consumer secret is required for WooCommerce."
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            {MAPPING_FIELDS.map(({ key, label }) => (
              <Paper
                key={key}
                elevation={0}
                sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mt: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: "#202531", mb: 2 }}
                >
                  {label}
                </Typography>
                <Grid container spacing={2}>
                  {MAPPING_PROPERTIES.map(({ key: propertyKey, label: propertyLabel }) => (
                    <Grid item xs={12} md={4} key={propertyKey}>
                      <TextField
                        fullWidth
                        label={propertyLabel}
                        value={form.wooCommerce[key][propertyKey]}
                        onChange={(event) =>
                          handleMappingChange(
                            key,
                            propertyKey,
                            event.target.value
                          )
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </>
        ) : null}

        {form.provider === "FluentCommerce" ? (
          <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mt: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: "#202531", mb: 2 }}
            >
              FluentCommerce connection
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Server URL"
                  type="url"
                  value={form.fluentCommerce.storeUrl}
                  onChange={(event) => {
                    setSaveError("");
                    setForm((current) => ({
                      ...current,
                      fluentCommerce: {
                        ...current.fluentCommerce,
                        storeUrl: event.target.value,
                      },
                    }));
                  }}
                  error={showValidation && isServerUrlMissing}
                  helperText={
                    showValidation && isServerUrlMissing
                      ? "Server URL is required for FluentCommerce."
                      : undefined
                  }
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CredentialField
                  label="Client ID"
                  visiblePrefixLength={3}
                  value={form.clientId}
                  onChange={(event) => {
                    setSaveError("");
                    setForm((current) => ({
                      ...current,
                      clientId: event.target.value,
                    }));
                  }}
                  revealed={showClientId}
                  toggleDisabled={!hasSavedClientId}
                  onToggle={() => setShowClientId((current) => !current)}
                  required
                  error={showValidation && isClientIdMissing}
                  helperText={
                    showValidation && isClientIdMissing
                      ? "Client ID is required for FluentCommerce."
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CredentialField
                  label="Client secret"
                  visiblePrefixLength={3}
                  value={form.clientSecret}
                  onChange={(event) => {
                    setSaveError("");
                    setForm((current) => ({
                      ...current,
                      clientSecret: event.target.value,
                    }));
                  }}
                  revealed={showClientSecret}
                  toggleDisabled={!hasSavedClientSecret}
                  onToggle={() => setShowClientSecret((current) => !current)}
                  required
                  error={showValidation && isClientSecretMissing}
                  helperText={
                    showValidation && isClientSecretMissing
                      ? "Client secret is required for FluentCommerce."
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CredentialField
                  label="Cloud tenant ID"
                  visiblePrefixLength={3}
                  value={form.cloudTenantId}
                  onChange={(event) => {
                    setSaveError("");
                    setForm((current) => ({
                      ...current,
                      cloudTenantId: event.target.value,
                    }));
                  }}
                  revealed={showCloudTenantId}
                  toggleDisabled={!hasSavedCloudTenantId}
                  onToggle={() => setShowCloudTenantId((current) => !current)}
                  required
                  error={showValidation && isCloudTenantIdMissing}
                  helperText={
                    showValidation && isCloudTenantIdMissing
                      ? "Cloud tenant ID is required for FluentCommerce."
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        ) : null}

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {!form.provider ? (
            <Typography variant="body2" color="error">
              Select a provider before saving.
            </Typography>
          ) : null}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.provider || !dirty || saving}
            sx={{
              minWidth: 132,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 800,
              background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
            }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Save settings"}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={confirmShopifyOpen}
        onClose={() => setConfirmShopifyOpen(false)}
      >
        <DialogTitle>Switch to Shopify?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching providers will clear all WooCommerce connection and field
            mapping values. This change is not sent until you save.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmShopifyOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmShopify}>
            Switch and clear
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessOpen(false)}
          sx={{ width: "100%" }}
        >
          Order tracking settings saved successfully.
        </Alert>
      </Snackbar>
    </>
  );
}
